import React, { useEffect, useState } from "react";
import { Button, Upload, Row, Col, Alert, Space, Checkbox } from "antd";
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import styles from "./AnnexureUpload.module.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import { message } from "antd";
import {
  deleteUploadedDocument,
  downloadDocument,
} from "../../../../apis/patientService";
import RightSideFilePreview from "./RightSideFilePreview";
import FilePreviewModal from "../../PreAuth-query/FilePreviewModal";
import {
  errorMessage,
  successMessage,
} from "../../../../utils/notificationUtils";

interface IAnnexureUploadProps {
  file?: File | null;
  setFile?: (file: File | null) => void;
  onNext?: () => void;
  onCancel?: () => void;
  caseInfo?: {
    caseId?: string;
    patientName?: string;
  };
  documentTypes: any;
  uploadedDocuments: {
    documentUrl: string;
    documentType: string;
    documentLabelType: string;
    documentName: string;
    deficiencyCheckStatus: any;
    id: number;
  }[];
  setUploadedDocuments: any;
  groupDocumentsByType?: any;
  activeKey?: any;
  handleSubmitAnnexure: any;
  isSaving: boolean;
  unsignedDocumentsCount: any;
  isAnnextureUploaded?: boolean;
  isLastUnlistedProcedureCompleted: boolean;
  sentToQCAnnexture: boolean;
  handleSentToQcAnnexure: any;
  isIntimating: boolean;
  handleManualSubmitAnnexure: any;
}

const AnnexureUpload: React.FC<IAnnexureUploadProps> = ({
  file,
  setFile,
  onNext,
  onCancel,
  caseInfo = {
    caseId: "CASE #882-104",
    patientName: "MAHIMA SHARMA",
  },
  documentTypes,
  uploadedDocuments,
  setUploadedDocuments,
  groupDocumentsByType,
  activeKey,
  handleSubmitAnnexure,
  isSaving,
  unsignedDocumentsCount,
  isAnnextureUploaded,
  isLastUnlistedProcedureCompleted,
  sentToQCAnnexture,
  handleSentToQcAnnexure,
  isIntimating,
  handleManualSubmitAnnexure
}) => {
  const [annexureLetterUrl, setAnnexureLetterUrl] = useState<any>(null);
  const [annexureLetterLabelId, setAnnexureLetterLabelId] = useState<any>(null);
  const [acknowledgeTerms, setAcknowledgeTerms] = useState(false);
  const { auth, patients, enhancement } = useSelector(
    (state: RootState) => state,
  );
  const dispatch = useDispatch<AppDispatch>();

  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });

  useEffect(() => {
    const annexureDocument = documentTypes?.find((document: any) =>
      document?.label
        ?.toLowerCase() === "Annexture Document"?.toLowerCase(),
    );
    setAnnexureLetterUrl(annexureDocument?.documentList?.[0]?.documentUrl);
    setAnnexureLetterLabelId(annexureDocument?.labelId);
  }, [documentTypes]);

  const handleFileChange = (documentType: string) => (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }

    if (e.file?.type !== "application/pdf") {
      errorMessage("File Must be PDF only");
      return;
    }

    if (e?.file) {
      setSelectedFile({ file: e.file, documentType: documentType });
      setPreviewFile({
        url: URL.createObjectURL(e.file),
        name: e.file.name,
        showUpload: true,
        documentType: documentType,
      });
      setPreviewModalVisible(true);
    }
    return e?.target?.files;
  };

  const handleUpload = async () => {
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
      );
    }
    CollectPostData.append("documentType", previewFile.documentType);
    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file);
    }
    try {
      const documentRes = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        }),
      ).unwrap();

      const updatedDocuments = [...uploadedDocuments, ...documentRes];
      setUploadedDocuments(updatedDocuments);
      setPreviewModalVisible(false);
      message.success(
        `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
      );

      if(documentRes?.[0]?.documentLabelType === "Annexture Document") {
        handleManualSubmitAnnexure(documentRes?.[0]);
        setAnnexureLetterUrl(documentRes?.[0]?.documentUrl);
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number,
  ) => {
    const deleteDocument = uploadedDocuments?.find(
      (doc, index) => doc?.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };
    const newUpdatedDocumentList = uploadedDocuments?.filter(
      (doc, index) => doc?.documentUrl !== documentUrl,
    );
    setUploadedDocuments(newUpdatedDocumentList);
    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
        // message.success("Document Deleted Successfully");
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDownload = async () => {
    try {
      try {
        await downloadDocument("Annexure Letter", annexureLetterUrl);
        successMessage("document downloaded successfully");
      } catch (err) {
        errorMessage("Please, Try again later");
      }
    } catch (err) {}
  };

  return (
    <div className={styles.annexureContent}>
      {/* <div className={styles.annexureHeader}>
        <p className={styles.annexureSubtitle}>
          {caseInfo.caseId} • PATIENT: {caseInfo.patientName}
        </p>
      </div> */}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24}>
          <div className={styles.annexureDownloadSection}>
            <div
              className={styles.annexureHeaderCardIcon}
              style={{ margin: "auto" }}
            >
              <DownloadOutlined style={{ textAlign: "center" }} />
            </div>
            <h3 className={styles.annexureSectionTitle}>
              Annexure Letter Generated
            </h3>
            <p className={styles.annexureDescription}>
              Retrieve the PDF with auto-filled patient and procedure data.
            </p>

            {annexureLetterUrl ? (
              <div className={styles.annexureDownloadBox}>
                <p className={styles.annexureDownloadText}>
                  Download Auto-Generated Annexure
                </p>

                <Button
                  type="primary"
                  onClick={handleDownload}
                  size="large"
                  className={styles.annexureDownloadBtn}
                >
                  Start Download
                </Button>
              </div>
            ) : (
              <div className={styles.annexureDownloadBox}>
                <Upload
                  multiple={false}
                  showUploadList={false}
                  onChange={handleFileChange(annexureLetterLabelId)}
                  beforeUpload={() => false}
                  className={styles.annexureUpload}
                >
                  <div className={styles.annexureUploadContent}>
                    <CloudUploadOutlined
                      className={styles.annexureUploadIcon}
                    />
                    <p className={styles.annexureUploadText}>
                       Upload Auto-Generated Annexure
                    </p>
                    <Button type="primary" className={styles.annexureBrowseBtn}>
                      Browse
                    </Button>
                  </div>
                </Upload>
              </div>
            )}

            {/* {isDownloaded && (
              <Alert
                message="Downloaded Successfully"
                description="PDF has been downloaded. Complete the required workflow and upload the signed document."
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )} */}
          </div>
        </Col>

        {/* <Col xs={24} sm={24} md={10}>
          <div className={styles.annexureWorkflowCard}>
            <h4 className={styles.annexureStepTitle}>REQUIRED WORKFLOW</h4>
            <ol className={styles.annexureStepList}>
              <li>
                Apply <strong>Digital Signature</strong> to the downloaded PDF.
              </li>
              <li>
                Obtain physical <strong>Doctor's Signature</strong> on printed
                copy.
              </li>
              <li>
                Apply mandatory <strong>Hospital Stamp</strong> on all pages.
              </li>
              <li>
                Scan and <strong>Reupload</strong> as high-quality PDF.
              </li>
            </ol>

            <div
              style={{
                background: "#FFDADA66",
                borderRadius: "8px",
                padding: "16px 32px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <WarningOutlined style={{ color: "black" }} />
                <p style={{ margin: 0 }}>
                  <strong>Notice</strong>
                </p>
              </div>
              <span style={{ color: "#920028", fontSize: 12 }}>
                Unauthorized reproduction or distribution of this official
                annexure is prohibited under Medical Privacy Act.
              </span>
            </div>
          </div>
        </Col> */}
      </Row>

      <Row gutter={[16, 16]} style={{ height: "130vh" }}>
        {documentTypes
          ?.filter((docType: any) =>
            docType?.label
              ?.toLowerCase()
              ?.includes("Annexture Document for Portal"?.toLowerCase()),
          )
          ?.map((docType: any) => (
            <Col xs={24} key={docType?.labelId}>
              <div
                className={styles.annexureUploadSection}
                style={{ height: "100%" }}
              >
                <h3 className={styles.annexureSectionTitle}>
                  Upload Signed Annexure
                </h3>

                {groupDocumentsByType(uploadedDocuments)?.[docType?.labelId] &&
                groupDocumentsByType(uploadedDocuments)?.[docType?.labelId]
                  ?.length > 0 ? (
                  <RightSideFilePreview
                    documents={
                      groupDocumentsByType(uploadedDocuments)?.[
                        docType?.labelId
                      ]
                    }
                    handleDeleteList={handleDeleteList}
                    activeKey={activeKey}
                    uploadedDocuments={uploadedDocuments}
                    setUploadedDocuments={setUploadedDocuments}
                    isSubmitted={
                      isLastUnlistedProcedureCompleted ||
                      (auth?.role?.role === "FO_STAFF" && sentToQCAnnexture)
                    }
                  />
                ) : (
                  <div className={styles.annexureUploadBox}>
                    <Upload
                      multiple={false}
                      showUploadList={false}
                      onChange={handleFileChange(docType?.labelId)}
                      beforeUpload={() => false}
                      className={styles.annexureUpload}
                    >
                      <div className={styles.annexureUploadContent}>
                        <CloudUploadOutlined
                          className={styles.annexureUploadIcon}
                        />
                        <p className={styles.annexureUploadText}>
                          Drop signed Annexure here
                        </p>
                        <p className={styles.annexureUploadUploadHint}>
                          Ensure Stamp is clearly visible.
                        </p>
                        <Button
                          type="primary"
                          className={styles.annexureBrowseBtn}
                        >
                          Browse
                        </Button>
                      </div>
                    </Upload>
                  </div>
                )}
              </div>
            </Col>
          ))}

        <Col xs={24}>
          <div className={styles.annexureTermsBox}>
            <Checkbox
              checked={true}
              onChange={(e) => setAcknowledgeTerms(e.target.checked)}
            >
              <span className={styles.annexureTermsText}>
                I acknowledge that all documents are complete and legally
                signed.
              </span>
            </Checkbox>
          </div>
        </Col>
      </Row>

      <div className={styles.annexureActionButtons}>
        <Space>
          {onCancel && (
            <Button size="large" onClick={onCancel}>
              Back
            </Button>
          )}

          {/* {uploadedDocuments?.some((doc: any) =>
            doc?.documentLabelType
              ?.toLowerCase()
              ?.includes("Annexture Document for Portal"?.toLowerCase()),
          ) &&
            unsignedDocumentsCount === 0 &&
            isAnnextureUploaded && (
              <Button
                size="large"
                onClick={onNext}
                style={{ background: "#00549e", color: "#fff" }}
              >
                Next
              </Button>
            )} */}

          {auth?.role?.role === "FO_STAFF" &&
          sentToQCAnnexture ? null : !isLastUnlistedProcedureCompleted ? (
            <Button
              type="primary"
              size="large"
              loading={isSaving}
              // disabled={!file || fileList.length === 0}
              onClick={() => handleSubmitAnnexure(false)}
              style={{ background: "#F1F5F9", color: "#00549e" }}
            >
              {"Save As Draft"}
            </Button>
          ) : (
            <></>
          )}

          {/* {auth?.role?.role === "FO_STAFF" &&
            !sentToQCAnnexture &&
            !isLastUnlistedProcedureCompleted && (
              <Button
                type="primary"
                size="large"
                loading={isIntimating}
                // disabled={!file || fileList.length === 0}
                onClick={handleSentToQcAnnexure}
                style={{ backgroundColor: "#00549e", color: "#F1F5F9" }}
              >
                {"Sent To QC"}
              </Button>
            )} */}

          {auth?.role?.role === "FO_STAFF" &&
            !isLastUnlistedProcedureCompleted && (
              <Button
                type="primary"
                size="large"
                loading={isIntimating}
                // disabled={!allChecksPass || !acknowledgeSubmission}
                onClick={() => handleSubmitAnnexure(true)}
                style={{ background: "#00549E", color: "white" }}
              >
                Submit to ECHS Portal
              </Button>
            )}
        </Space>
      </div>

      <FilePreviewModal
        isVisible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        fileUrl={previewFile.url}
        fileName={previewFile.name}
        documentType={previewFile.documentType}
        handleUpload={handleUpload}
        showUploadButton={previewFile.showUpload}
        key={selectedFile?.file?.lastModified || "no-file"}
        isLoading={enhancement?.isLoading}
      />
    </div>
  );
};

export default AnnexureUpload;
